import React, { useEffect, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import Select from 'react-select';

import Modal from '../shared/components/core-ui/modal/modal.component';
import { birdToast } from '../shared/components/core-ui/toast';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import { createHousekeepingOrder } from './data.service';
import { CreateHousekeepingOrderFormState, HousekeepingAssigneeOption } from './types';

interface SelectOption {
  label: string;
  value: string;
}

interface HouseKeepingCreateModalProps {
  open: boolean;
  projectOptions: SelectOption[];
  unitTypeOptions: SelectOption[];
  userOptions: HousekeepingAssigneeOption[];
  onClose: () => void;
  onCreated: () => void;
}

const initialFormState: CreateHousekeepingOrderFormState = {
  projectId: '',
  unitTypeIds: [],
  assignedToId: '',
  orderDate: null
};

const HouseKeepingCreateModal: React.FC<HouseKeepingCreateModalProps> = ({
  open,
  projectOptions,
  unitTypeOptions,
  userOptions,
  onClose,
  onCreated
}) => {
  const [form, setForm] = useState<CreateHousekeepingOrderFormState>(initialFormState);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initialFormState);
    }
  }, [open]);

  const filteredUnitTypeOptions = useMemo(
    () =>
      unitTypeOptions.filter(
        (option: SelectOption & { projectId?: string }) =>
          !form.projectId || String(option.projectId || '') === String(form.projectId)
      ),
    [form.projectId, unitTypeOptions]
  );

  const selectedProjectOption =
    projectOptions.find((option) => option.value === form.projectId) || null;
  const selectedUnitTypeOptions = filteredUnitTypeOptions.filter((option) =>
    form.unitTypeIds.includes(option.value)
  );
  const selectedUserOption =
    userOptions.find((option) => option.value === form.assignedToId) || null;

  const handleSubmit = () => {
    const hasProject = Boolean(form.projectId);
    const hasUnitTypes = form.unitTypeIds.length > 0;
    const hasOrderDate = Boolean(form.orderDate);

    if (!hasProject || !hasUnitTypes || !hasOrderDate) {
      birdToast.error('اختر المشروع وأنواع الوحدات وتاريخ/وقت الطلب.');
      return;
    }

    setSaving(true);

    const promise = createHousekeepingOrder({
      projectId: form.projectId,
      unitTypeIds: form.unitTypeIds,
      assignedToId: form.assignedToId || undefined,
      orderDate: form.orderDate?.getTime() || Date.now()
    }).then((response) => {
      if (isErrorInfo(response)) {
        setSaving(false);
        throw response;
      }

      onClose();
      onCreated();
      setSaving(false);
      return response;
    });

    birdToast.promise(promise, {
      loadingMessage: 'جاري إنشاء طلب النظافة...',
      successMessage: 'تم إنشاء طلب النظافة بنجاح',
      errorFailedMessage: 'حدث خطأ أثناء إنشاء الطلب. حاول مرة أخرى.'
    });
  };

  return (
    <Modal
      config={{
        showModal: open,
        fullScreen: false,
        showClose: true,
        maxWidth: 'sm'
      }}
      eventHandlers={{
        closeModalHandler: onClose
      }}
    >
      <div className="p-6" dir="rtl" lang="ar">
        <h2 className="text-2xl text-center font-bold text-slate-900">إنشاء طلب نظافة جديد</h2>

        <div className="mt-5 space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-base font-gilroy-medium font-medium text-neutral-500">المشروع</p>
            <div className="mt-2">
              <Select
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                options={projectOptions}
                value={selectedProjectOption}
                isClearable
                placeholder="اختر المشروع"
                onChange={(value: any) => {
                  const projectId = value?.value || '';
                  setForm((current) => ({
                    ...current,
                    projectId,
                    unitTypeIds: current.unitTypeIds.filter((unitTypeId) =>
                      unitTypeOptions.some(
                        (option: SelectOption & { projectId?: string }) =>
                          option.value === unitTypeId &&
                          (!projectId || String(option.projectId || '') === String(projectId))
                      )
                    )
                  }));
                }}
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-base font-gilroy-medium font-medium text-neutral-500">
              أنواع الوحدات
            </p>
            <div className="mt-2">
              <Select
                isMulti
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                options={filteredUnitTypeOptions}
                value={selectedUnitTypeOptions}
                placeholder="اختر نوع وحدة أو أكثر"
                onChange={(values: any) =>
                  setForm((current) => ({
                    ...current,
                    unitTypeIds: (values || []).map((value: any) => value.value)
                  }))
                }
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-base font-gilroy-medium font-medium text-neutral-500">
              موظف النظافة
            </p>
            <div className="mt-2">
              <Select
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                options={userOptions}
                value={selectedUserOption}
                placeholder="اختر موظف النظافة"
                onChange={(value: any) =>
                  setForm((current) => ({
                    ...current,
                    assignedToId: value?.value || ''
                  }))
                }
                isClearable
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-base font-gilroy-medium font-medium text-neutral-500">
              تاريخ ووقت الطلب
            </p>
            <div className="mt-2">
              <ReactDatePicker
                selected={form.orderDate}
                onChange={(value) => {
                  const orderDate = value instanceof Date || value === null ? value : null;
                  setForm((current) => ({ ...current, orderDate }));
                }}
                showTimeSelect
                timeIntervals={15}
                placeholderText="اختر التاريخ والوقت"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-emerald-500"
                dateFormat="yyyy-MM-dd hh:mm aa"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-lg font-semibold text-slate-700"
          >
            إلغاء
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleSubmit}
            className="rounded-xl bg-emerald-600 px-4 py-2 text-lg font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'جاري الإنشاء...' : 'إنشاء الطلب'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default HouseKeepingCreateModal;
